import {
  Glass,
  PillChip,
  Dot,
  ScreenLabel,
  DisplayText,
  BodyText,
  alpha,
  borderAlpha,
} from "../../components/mobile/primitives";

// ─── Shared constants ─────────────────────────────────────────────────────

const STEPS_COLOR = "var(--color-kin-green-soft)";
const HOMOCYSTEINE_COLOR = "var(--color-kin-rose)";

const stepsData = [
  { date: "Oct", value: 7200 },
  { date: "Nov", value: 8400 },
  { date: "Dec", value: 6100 },
  { date: "Jan", value: 9300 },
  { date: "Feb", value: 8800 },
  { date: "Mar", value: 10200 },
];

const homocysteineData = [
  { date: "Oct", value: 11.2 },
  { date: "Dec", value: 9.8 },
  { date: "Feb", value: 12.4 },
];

// ─── Shared components ────────────────────────────────────────────────────

function BackNav({ label = "Back", color = "var(--color-kin-green-soft)" }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0" }}>
      <svg
        width="8"
        height="12"
        viewBox="0 0 8 12"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6.5 1l-5 5 5 5" />
      </svg>
      <BodyText size={10} color={color} weight={600}>
        {label}
      </BodyText>
    </div>
  );
}

function Chevron({ color = "var(--color-kin-olive-light)" }) {
  return (
    <svg
      width="8"
      height="12"
      viewBox="0 0 8 12"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1.5 1l5 5-5 5" />
    </svg>
  );
}

function PickerButton({ label, value, color, active = false }) {
  return (
    <div
      style={{
        flex: 1,
        padding: "8px 10px",
        borderRadius: 14,
        background: active ? alpha(color, "0C") : "rgba(254,253,250,0.58)",
        border: active ? borderAlpha(color, "30") : "1px solid rgba(255,255,255,0.35)",
        backdropFilter: "blur(24px)",
        display: "flex",
        alignItems: "center",
        gap: 7,
      }}
    >
      {active && <Dot color={color} size={6} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <BodyText
          size={7}
          color="var(--color-kin-muted)"
          weight={600}
          style={{ letterSpacing: "0.08em", textTransform: "uppercase" }}
        >
          {label}
        </BodyText>
        <BodyText
          size={9.5}
          weight={active ? 700 : 400}
          color={active ? color : "var(--color-kin-muted)"}
        >
          {value}
        </BodyText>
      </div>
      <svg
        width="7"
        height="5"
        viewBox="0 0 7 5"
        fill="none"
        stroke={active ? color : "var(--color-kin-olive-light)"}
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M1 1l2.5 2.5L6 1" />
      </svg>
    </div>
  );
}

// ─── Screen 1: Health Data Browser ────────────────────────────────────────
// Child screen of Profile — user taps a health data category tile on the Profile tab
// to arrive here. Back nav returns to Me.

export function FlowHealthDataBrowser() {
  const vitals = [
    {
      label: "Steps",
      value: "10,240",
      trend: "+8%",
      color: "var(--color-kin-green-soft)",
      unit: "/day",
    },
    { label: "Sleep", value: "7.2h", trend: "-3%", color: "var(--color-kin-gold)", unit: "avg" },
    { label: "RHR", value: "62", trend: "-2%", color: "var(--color-kin-rose)", unit: "bpm" },
    { label: "HRV", value: "48ms", trend: "+12%", color: "var(--color-kin-lavender)", unit: "avg" },
  ];

  const biomarkers = [
    {
      name: "Homocysteine",
      value: "12.4",
      unit: "umol/L",
      flag: "HIGH",
      color: "var(--color-kin-rose)",
      cat: "Metabolic",
    },
    {
      name: "LDL Cholesterol",
      value: "128",
      unit: "mg/dL",
      flag: null,
      color: "var(--color-kin-green-soft)",
      cat: "Lipids",
    },
    {
      name: "Vitamin D",
      value: "18",
      unit: "ng/mL",
      flag: "LOW",
      color: "var(--color-kin-gold-dark)",
      cat: "Vitamins",
    },
    {
      name: "TSH",
      value: "2.1",
      unit: "mIU/L",
      flag: null,
      color: "var(--color-kin-green-soft)",
      cat: "Thyroid",
    },
    {
      name: "Ferritin",
      value: "45",
      unit: "ng/mL",
      flag: null,
      color: "var(--color-kin-green-soft)",
      cat: "Hematology",
    },
  ];

  return (
    <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Back nav to Me */}
      <BackNav label="Profile" color="var(--color-kin-green-soft)" />

      {/* Header */}
      <div style={{ paddingLeft: 4 }}>
        <DisplayText size={28}>Health Data</DisplayText>
        <BodyText size={9} color="var(--color-kin-muted)" weight={400} style={{ marginTop: 2 }}>
          6 metrics &middot; 5 biomarkers &middot; Updated today
        </BodyText>
      </div>

      {/* Vitals grid — each card tappable */}
      <ScreenLabel>Wearables</ScreenLabel>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {vitals.map((v) => (
          <Glass key={v.label} style={{ borderRadius: 16, padding: "10px 12px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 4,
              }}
            >
              <BodyText
                size={8}
                color="var(--color-kin-muted)"
                weight={600}
                style={{ letterSpacing: "0.08em", textTransform: "uppercase" }}
              >
                {v.label}
              </BodyText>
              <Chevron />
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
              <DisplayText size={20}>{v.value}</DisplayText>
              <BodyText
                size={8}
                color={
                  v.trend.startsWith("+") ? "var(--color-kin-green-soft)" : "var(--color-kin-rose)"
                }
                weight={600}
              >
                {v.trend}
              </BodyText>
            </div>
            {/* Mini sparkline */}
            <svg width="80" height="16" viewBox="0 0 80 16" style={{ marginTop: 4 }}>
              <path
                d="M0 12 Q20 8 30 10 Q40 4 55 6 Q65 2 80 4"
                fill="none"
                stroke={v.color}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </Glass>
        ))}
      </div>

      {/* Biomarkers — browseable list */}
      <ScreenLabel style={{ marginTop: 4 }}>Biomarkers</ScreenLabel>
      <Glass style={{ borderRadius: 16 }}>
        <div style={{ padding: "0 12px" }}>
          {biomarkers.map((m, i) => (
            <div
              key={m.name}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "9px 0",
                borderBottom: i < biomarkers.length - 1 ? "1px solid rgba(0,0,0,0.05)" : "none",
              }}
            >
              <Dot color={m.color} size={6} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <BodyText size={9.5} weight={600}>
                  {m.name}
                </BodyText>
                <BodyText size={7.5} color="var(--color-kin-muted)" weight={400}>
                  {m.cat}
                </BodyText>
              </div>
              <BodyText size={9} weight={500}>
                {m.value}
              </BodyText>
              <BodyText size={7.5} color="var(--color-kin-muted)" weight={400}>
                {m.unit}
              </BodyText>
              {m.flag && (
                <div
                  style={{
                    padding: "1px 5px",
                    borderRadius: 6,
                    background:
                      m.flag === "HIGH" ? "rgba(255,107,107,0.12)" : "rgba(240,184,0,0.12)",
                  }}
                >
                  <BodyText
                    size={7}
                    color={
                      m.flag === "HIGH" ? "var(--color-kin-rose)" : "var(--color-kin-gold-dark)"
                    }
                    weight={700}
                  >
                    {m.flag}
                  </BodyText>
                </div>
              )}
              {!m.flag && <Chevron />}
            </div>
          ))}
        </div>
      </Glass>

      {/* Index series peek */}
      <ScreenLabel style={{ marginTop: 4 }}>Index Series</ScreenLabel>
      <Glass style={{ borderRadius: 16 }}>
        <div style={{ padding: "0 12px" }}>
          {[
            { name: "Daily protein", index: "Nutrition", color: "#ff8a4d" },
            { name: "Weekly exercise", index: "Activity", color: "var(--color-kin-green-soft)" },
          ].map((m, i) => (
            <div
              key={m.name}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "9px 0",
                borderBottom: i < 1 ? "1px solid rgba(0,0,0,0.05)" : "none",
              }}
            >
              <div
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 6,
                  background: alpha(m.color, "14"),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  stroke={m.color}
                  strokeWidth="1.2"
                  strokeLinecap="round"
                >
                  <polyline points="1,7 3.5,4 6,5.5 9,2" />
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <BodyText size={9.5} weight={600}>
                  {m.name}
                </BodyText>
                <BodyText size={7.5} color="var(--color-kin-muted)" weight={400}>
                  {m.index}
                </BodyText>
              </div>
              <Chevron />
            </div>
          ))}
        </div>
      </Glass>
    </div>
  );
}

// ─── Screen 2: Steps Detail — with "Compare with..." action ──────────────
// User tapped Steps from the hub. They see the full chart, trend stats, and
// a "Compare with..." button that opens the comparison flow.

export function FlowStepsDetail() {
  const chartW = 220;
  const chartH = 90;
  const padL = 28;
  const padR = 8;
  const padT = 10;
  const padB = 16;
  const plotW = chartW - padL - padR;
  const plotH = chartH - padT - padB;

  const vals = stepsData.map((d) => d.value);
  const minV = Math.min(...vals) - 500;
  const maxV = Math.max(...vals) + 500;
  const range = maxV - minV;

  const points = stepsData.map((d, i) => ({
    x: padL + (i / (stepsData.length - 1)) * plotW,
    y: padT + plotH * (1 - (d.value - minV) / range),
    ...d,
  }));

  const pathD = points.reduce((d, p, i) => {
    if (i === 0) return `M ${p.x} ${p.y}`;
    const prev = points[i - 1];
    const cpx1 = prev.x + (p.x - prev.x) * 0.4;
    const cpx2 = prev.x + (p.x - prev.x) * 0.6;
    return `${d} C ${cpx1} ${prev.y} ${cpx2} ${p.y} ${p.x} ${p.y}`;
  }, "");

  const yLabels = [
    { label: "10k", y: padT + plotH * (1 - (10000 - minV) / range) },
    { label: "8k", y: padT + plotH * (1 - (8000 - minV) / range) },
    { label: "6k", y: padT + plotH * (1 - (6000 - minV) / range) },
  ];

  return (
    <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 12 }}>
      <BackNav label="Health Data" />

      {/* Hero */}
      <div>
        <ScreenLabel style={{ marginBottom: 3 }}>Wearable</ScreenLabel>
        <DisplayText size={24}>Steps</DisplayText>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 4 }}>
          <DisplayText size={36} color={STEPS_COLOR}>
            10,240
          </DisplayText>
          <BodyText size={10} color="var(--color-kin-muted)" weight={500}>
            today
          </BodyText>
        </div>
        <BodyText
          size={9}
          color="var(--color-kin-green-soft)"
          weight={600}
          style={{ marginTop: 3 }}
        >
          +8% vs last 7 days
        </BodyText>
      </div>

      {/* Chart */}
      <Glass style={{ borderRadius: 18 }}>
        <div style={{ padding: "12px 6px 8px" }}>
          <ScreenLabel style={{ marginBottom: 8, paddingLeft: 4 }}>Trend</ScreenLabel>
          <svg width={chartW} height={chartH} viewBox={`0 0 ${chartW} ${chartH}`}>
            {yLabels.map((yl) => (
              <g key={yl.label}>
                <line
                  x1={padL}
                  y1={yl.y}
                  x2={chartW - padR}
                  y2={yl.y}
                  stroke="rgba(0,0,0,0.05)"
                  strokeWidth={0.5}
                />
                <text
                  x={padL - 4}
                  y={yl.y + 3}
                  textAnchor="end"
                  fontSize={6.5}
                  fill="var(--color-kin-muted)"
                  fontFamily="Manrope, system-ui"
                  fontWeight={500}
                >
                  {yl.label}
                </text>
              </g>
            ))}

            <path
              d={`${pathD} L ${points[points.length - 1].x} ${chartH - padB} L ${points[0].x} ${chartH - padB} Z`}
              fill={alpha(STEPS_COLOR, "10")}
            />
            <path
              d={pathD}
              fill="none"
              stroke={STEPS_COLOR}
              strokeWidth={2}
              strokeLinecap="round"
            />
            {points.map((p, i) => (
              <circle
                key={i}
                cx={p.x}
                cy={p.y}
                r={3}
                fill={STEPS_COLOR}
                stroke="#fff"
                strokeWidth={1.5}
              />
            ))}
            {points.map((p, i) => (
              <text
                key={`l${i}`}
                x={p.x}
                y={chartH - 2}
                textAnchor="middle"
                fontSize={6.5}
                fill="var(--color-kin-muted)"
                fontFamily="Manrope, system-ui"
              >
                {p.date}
              </text>
            ))}
          </svg>
        </div>
      </Glass>

      {/* Time range */}
      <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
        <PillChip>30D</PillChip>
        <PillChip active color="var(--color-kin-green-soft)">
          90D
        </PillChip>
        <PillChip>1Y</PillChip>
        <PillChip>ALL</PillChip>
      </div>

      {/* Stats row */}
      <div style={{ display: "flex", gap: 8 }}>
        <Glass style={{ flex: 1, borderRadius: 14, padding: "8px 10px" }}>
          <BodyText
            size={7}
            color="var(--color-kin-muted)"
            weight={600}
            style={{ letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}
          >
            Average
          </BodyText>
          <BodyText size={11} weight={700}>
            8,383
          </BodyText>
          <BodyText size={7.5} color="var(--color-kin-muted)" weight={400}>
            /day
          </BodyText>
        </Glass>
        <Glass style={{ flex: 1, borderRadius: 14, padding: "8px 10px" }}>
          <BodyText
            size={7}
            color="var(--color-kin-muted)"
            weight={600}
            style={{ letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}
          >
            Best day
          </BodyText>
          <BodyText size={11} weight={700}>
            10,240
          </BodyText>
          <BodyText size={7.5} color="var(--color-kin-muted)" weight={400}>
            Mar 18
          </BodyText>
        </Glass>
      </div>

      {/* Compare with... — the entry point to comparison */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          borderRadius: 16,
          background: "linear-gradient(135deg, rgba(91,122,102,0.06), rgba(165,144,255,0.06))",
          border: "1px solid rgba(91,122,102,0.14)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              width: 26,
              height: 26,
              borderRadius: 8,
              background: "rgba(91,122,102,0.10)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 20V4" stroke={STEPS_COLOR} />
              <path d="M21 20V4" stroke="var(--color-kin-lavender)" />
              <polyline points="6,14 10,10 14,12 18,7" stroke={STEPS_COLOR} strokeWidth="1.5" />
              <polyline
                points="6,16 10,13 14,15 18,11"
                stroke="var(--color-kin-lavender)"
                strokeWidth="1.5"
                strokeDasharray="2 1.5"
              />
            </svg>
          </div>
          <div>
            <BodyText size={10} weight={700}>
              Compare with...
            </BodyText>
            <BodyText size={8} color="var(--color-kin-muted)" weight={400}>
              Overlay another metric
            </BodyText>
          </div>
        </div>
        <Chevron color="var(--color-kin-green-soft)" />
      </div>
    </div>
  );
}

// ─── Screen 3: Metric Picker Modal ────────────────────────────────────────
// Appears after tapping "Compare with..." — choose what to overlay.

export function FlowMetricPicker() {
  return (
    <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Modal handle */}
      <div style={{ display: "flex", justifyContent: "center", paddingTop: 8 }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: "rgba(0,0,0,0.12)" }} />
      </div>

      <DisplayText size={20}>Compare with...</DisplayText>
      <BodyText size={9} color="var(--color-kin-muted)" weight={400}>
        Choose a metric to overlay against Steps.
      </BodyText>

      {/* Wearables section */}
      <ScreenLabel>Wearables</ScreenLabel>
      <Glass style={{ borderRadius: 16 }}>
        <div style={{ padding: "0 12px" }}>
          {[
            { name: "Steps", color: "var(--color-kin-green-soft)", disabled: true },
            { name: "Resting HR", color: "var(--color-kin-rose)" },
            { name: "HRV", color: "var(--color-kin-lavender)" },
            { name: "Sleep hours", color: "var(--color-kin-gold)" },
            { name: "Avg Heart Rate", color: "#ff8a4d" },
          ].map((m, i) => (
            <div
              key={m.name}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "9px 0",
                borderBottom: i < 4 ? "1px solid rgba(0,0,0,0.05)" : "none",
                opacity: m.disabled ? 0.35 : 1,
              }}
            >
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 7,
                  background: alpha(m.color, "14"),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Dot color={m.color} size={6} />
              </div>
              <BodyText size={10} weight={600} style={{ flex: 1 }}>
                {m.name}
              </BodyText>
              {m.disabled ? (
                <BodyText size={7.5} color="var(--color-kin-muted)" weight={500}>
                  Current
                </BodyText>
              ) : (
                <Chevron />
              )}
            </div>
          ))}
        </div>
      </Glass>

      {/* Biomarkers section */}
      <ScreenLabel>Biomarkers</ScreenLabel>
      <Glass style={{ borderRadius: 16 }}>
        <div style={{ padding: "0 12px" }}>
          {[
            {
              name: "Homocysteine",
              cat: "Metabolic",
              color: "var(--color-kin-green-soft)",
              selected: true,
            },
            { name: "LDL Cholesterol", cat: "Lipids", color: "#ff8a4d" },
            { name: "Vitamin D", cat: "Vitamins", color: "var(--color-kin-gold)" },
            { name: "TSH", cat: "Thyroid", color: "var(--color-kin-lavender)" },
          ].map((m, i) => (
            <div
              key={m.name}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "9px 0",
                borderBottom: i < 3 ? "1px solid rgba(0,0,0,0.05)" : "none",
                ...(m.selected
                  ? {
                      background: alpha(HOMOCYSTEINE_COLOR, "08"),
                      marginLeft: -12,
                      marginRight: -12,
                      paddingLeft: 12,
                      paddingRight: 12,
                    }
                  : {}),
              }}
            >
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 7,
                  background: alpha(m.color, "14"),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Dot color={m.color} size={6} />
              </div>
              <div style={{ flex: 1 }}>
                <BodyText size={10} weight={600}>
                  {m.name}
                </BodyText>
                <BodyText size={7.5} color="var(--color-kin-muted)" weight={400}>
                  {m.cat}
                </BodyText>
              </div>
              {m.selected ? (
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 9,
                    background: HOMOCYSTEINE_COLOR,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg
                    width="10"
                    height="8"
                    viewBox="0 0 10 8"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1 4l2.5 2.5L9 1" />
                  </svg>
                </div>
              ) : (
                <Chevron />
              )}
            </div>
          ))}
        </div>
      </Glass>

      {/* Index projections section */}
      <ScreenLabel>Index Series</ScreenLabel>
      <Glass style={{ borderRadius: 16 }}>
        <div style={{ padding: "0 12px" }}>
          {[
            { name: "Daily protein (sum)", index: "Nutrition Index", color: "#ff8a4d" },
            {
              name: "Weekly exercise hrs",
              index: "Activity Index",
              color: "var(--color-kin-green-soft)",
            },
          ].map((m, i) => (
            <div
              key={m.name}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "9px 0",
                borderBottom: i < 1 ? "1px solid rgba(0,0,0,0.05)" : "none",
              }}
            >
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 7,
                  background: alpha(m.color, "14"),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  stroke={m.color}
                  strokeWidth="1.2"
                  strokeLinecap="round"
                >
                  <polyline points="1,7 3.5,4 6,5.5 9,2" />
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <BodyText size={10} weight={600}>
                  {m.name}
                </BodyText>
                <BodyText size={7.5} color="var(--color-kin-muted)" weight={400}>
                  {m.index}
                </BodyText>
              </div>
              <Chevron />
            </div>
          ))}
        </div>
      </Glass>
    </div>
  );
}

// ─── Screen 4: Dual-Axis Comparison ───────────────────────────────────────
// Steps + Homocysteine overlaid with independent Y-axes.

export function FlowGraphExplorerDual() {
  const chartW = 222;
  const chartH = 110;
  const padL = 26;
  const padR = 26;
  const padT = 10;
  const padB = 18;
  const plotW = chartW - padL - padR;
  const plotH = chartH - padT - padB;

  const stepsVals = stepsData.map((d) => d.value);
  const stepsMin = Math.min(...stepsVals) - 500;
  const stepsMax = Math.max(...stepsVals) + 500;
  const stepsRange = stepsMax - stepsMin;

  const homoVals = homocysteineData.map((d) => d.value);
  const homoMin = Math.min(...homoVals) - 1;
  const homoMax = Math.max(...homoVals) + 1;
  const homoRange = homoMax - homoMin;

  const allDates = ["Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];

  const xScale = (date) => {
    const idx = allDates.indexOf(date);
    return padL + (idx / (allDates.length - 1)) * plotW;
  };

  const stepsPoints = stepsData.map((d) => ({
    x: xScale(d.date),
    y: padT + plotH * (1 - (d.value - stepsMin) / stepsRange),
    ...d,
  }));

  const homoPoints = homocysteineData.map((d) => ({
    x: xScale(d.date),
    y: padT + plotH * (1 - (d.value - homoMin) / homoRange),
    ...d,
  }));

  const stepsPath = stepsPoints.reduce((d, p, i) => {
    if (i === 0) return `M ${p.x} ${p.y}`;
    const prev = stepsPoints[i - 1];
    const cpx1 = prev.x + (p.x - prev.x) * 0.4;
    const cpx2 = prev.x + (p.x - prev.x) * 0.6;
    return `${d} C ${cpx1} ${prev.y} ${cpx2} ${p.y} ${p.x} ${p.y}`;
  }, "");

  const homoPath = homoPoints.reduce((d, p, i) => {
    if (i === 0) return `M ${p.x} ${p.y}`;
    const prev = homoPoints[i - 1];
    const cpx1 = prev.x + (p.x - prev.x) * 0.4;
    const cpx2 = prev.x + (p.x - prev.x) * 0.6;
    return `${d} C ${cpx1} ${prev.y} ${cpx2} ${p.y} ${p.x} ${p.y}`;
  }, "");

  const stepsYLabels = [
    { label: "10k", value: 10000 },
    { label: "8k", value: 8000 },
    { label: "6k", value: 6000 },
  ];

  const homoYLabels = [
    { label: "12", value: 12 },
    { label: "10", value: 10 },
    { label: "8", value: 8 },
  ];

  return (
    <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 12 }}>
      <BackNav label="Steps" />

      {/* Title with context */}
      <div>
        <DisplayText size={20}>Steps vs Homocysteine</DisplayText>
        <BodyText size={8.5} color="var(--color-kin-muted)" weight={400} style={{ marginTop: 2 }}>
          Dual-axis comparison &middot; 90 days
        </BodyText>
      </div>

      {/* Metric pickers — both active, swappable */}
      <div style={{ display: "flex", gap: 8 }}>
        <PickerButton label="Left axis" value="Steps" color={STEPS_COLOR} active />
        <PickerButton label="Right axis" value="Homocysteine" color={HOMOCYSTEINE_COLOR} active />
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 14, paddingLeft: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <Dot color={STEPS_COLOR} size={6} />
          <BodyText size={8.5} color={STEPS_COLOR} weight={600}>
            Steps
          </BodyText>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <Dot color={HOMOCYSTEINE_COLOR} size={6} />
          <BodyText size={8.5} color={HOMOCYSTEINE_COLOR} weight={600}>
            Homocysteine
          </BodyText>
        </div>
      </div>

      {/* Dual-axis chart */}
      <Glass style={{ borderRadius: 18 }}>
        <div style={{ padding: "12px 4px 8px" }}>
          <svg width={chartW} height={chartH} viewBox={`0 0 ${chartW} ${chartH}`}>
            {/* Left Y-axis labels (Steps — sage) */}
            {stepsYLabels.map((yl) => {
              const y = padT + plotH * (1 - (yl.value - stepsMin) / stepsRange);
              return (
                <g key={yl.label}>
                  <line
                    x1={padL}
                    y1={y}
                    x2={chartW - padR}
                    y2={y}
                    stroke="rgba(0,0,0,0.04)"
                    strokeWidth={0.5}
                  />
                  <text
                    x={padL - 4}
                    y={y + 3}
                    textAnchor="end"
                    fontSize={6}
                    fill={STEPS_COLOR}
                    fontFamily="Manrope, system-ui"
                    fontWeight={500}
                  >
                    {yl.label}
                  </text>
                </g>
              );
            })}

            {/* Right Y-axis labels (Homocysteine — rose) */}
            {homoYLabels.map((yl) => {
              const y = padT + plotH * (1 - (yl.value - homoMin) / homoRange);
              return (
                <text
                  key={yl.label}
                  x={chartW - padR + 4}
                  y={y + 3}
                  textAnchor="start"
                  fontSize={6}
                  fill={HOMOCYSTEINE_COLOR}
                  fontFamily="Manrope, system-ui"
                  fontWeight={500}
                >
                  {yl.label}
                </text>
              );
            })}

            {/* Steps area fill */}
            <path
              d={`${stepsPath} L ${stepsPoints[stepsPoints.length - 1].x} ${chartH - padB} L ${stepsPoints[0].x} ${chartH - padB} Z`}
              fill={alpha(STEPS_COLOR, "08")}
            />

            {/* Steps line */}
            <path
              d={stepsPath}
              fill="none"
              stroke={STEPS_COLOR}
              strokeWidth={2}
              strokeLinecap="round"
            />

            {/* Homocysteine line (dashed) */}
            <path
              d={homoPath}
              fill="none"
              stroke={HOMOCYSTEINE_COLOR}
              strokeWidth={2}
              strokeLinecap="round"
              strokeDasharray="4 2"
            />

            {/* Steps data points */}
            {stepsPoints.map((p, i) => (
              <circle
                key={`s${i}`}
                cx={p.x}
                cy={p.y}
                r={3}
                fill={STEPS_COLOR}
                stroke="#fff"
                strokeWidth={1.5}
              />
            ))}

            {/* Homocysteine data points (larger — sparse) */}
            {homoPoints.map((p, i) => (
              <circle
                key={`h${i}`}
                cx={p.x}
                cy={p.y}
                r={4.5}
                fill={HOMOCYSTEINE_COLOR}
                stroke="#fff"
                strokeWidth={1.5}
              />
            ))}

            {/* X-axis labels */}
            {allDates.map((date) => (
              <text
                key={date}
                x={xScale(date)}
                y={chartH - 3}
                textAnchor="middle"
                fontSize={6.5}
                fill="var(--color-kin-muted)"
                fontFamily="Manrope, system-ui"
              >
                {date}
              </text>
            ))}

            {/* Left axis line */}
            <line
              x1={padL}
              y1={padT}
              x2={padL}
              y2={chartH - padB}
              stroke={alpha(STEPS_COLOR, "40")}
              strokeWidth={1}
            />

            {/* Right axis line */}
            <line
              x1={chartW - padR}
              y1={padT}
              x2={chartW - padR}
              y2={chartH - padB}
              stroke={alpha(HOMOCYSTEINE_COLOR, "40")}
              strokeWidth={1}
            />
          </svg>
        </div>
      </Glass>

      {/* Time range chips */}
      <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
        <PillChip>30D</PillChip>
        <PillChip active color="var(--color-kin-green-soft)">
          90D
        </PillChip>
        <PillChip>1Y</PillChip>
        <PillChip>ALL</PillChip>
      </div>

      {/* Correlation insight */}
      <div
        style={{
          padding: "10px 12px",
          borderRadius: 14,
          background: "rgba(255,248,225,0.6)",
          border: "1px solid rgba(240,184,0,0.22)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <Dot color="var(--color-kin-gold-dark)" size={5} />
          <BodyText size={9} color="var(--color-kin-gold-dark)" weight={700}>
            Pattern
          </BodyText>
        </div>
        <BodyText
          size={8.5}
          color="var(--color-kin-muted)"
          weight={400}
          style={{ lineHeight: 1.5 }}
        >
          Steps trend up while homocysteine fluctuates. Sparse biomarker data (3 points) overlaid
          against dense daily wearable data.
        </BodyText>
      </div>

      {/* Summary stats row */}
      <div style={{ display: "flex", gap: 8 }}>
        <Glass style={{ flex: 1, borderRadius: 14, padding: "8px 10px" }}>
          <BodyText
            size={7}
            color="var(--color-kin-muted)"
            weight={600}
            style={{ letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}
          >
            Avg steps
          </BodyText>
          <BodyText size={11} weight={700} color={STEPS_COLOR}>
            8,383
          </BodyText>
          <BodyText size={7.5} color="var(--color-kin-muted)" weight={400}>
            /day
          </BodyText>
        </Glass>
        <Glass style={{ flex: 1, borderRadius: 14, padding: "8px 10px" }}>
          <BodyText
            size={7}
            color="var(--color-kin-muted)"
            weight={600}
            style={{ letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}
          >
            Latest Hcy
          </BodyText>
          <BodyText size={11} weight={700} color={HOMOCYSTEINE_COLOR}>
            12.4
          </BodyText>
          <BodyText size={7.5} color="var(--color-kin-muted)" weight={400}>
            umol/L
          </BodyText>
        </Glass>
      </div>
    </div>
  );
}
